<template>
  <div data-search-results-v="3.5.0">
    <!-- <DataPeek :data="searchResults" /> -->
    <div :class="$style.searchBlock">
      <div :class="$style.container">
        <SearchField
          :initial-query="searchValue"
          :class="$style.search"
          focus-on-mount
          @request-query="handleSearch"
          @clear-query="handleSearch"
        />
      </div>
    </div>
    <div :class="$style.resultsBlock">
      <div :class="$style.container">
        <div v-if="searchMode === 'single'" :class="$style.options">
          <div
            v-if="computedCategories.length > 1"
            :class="[
              $style.filters,
              activeFilterId === 'article' && $style.articleFilters,
            ]"
          >
            <span :class="[$style.filterLabel, 'u-type-p2']">
              {{
                data?.fields?.filterResultsLabel ||
                localizedStrings['filter-results'] ||
                'Filter Results'
              }}
            </span>
            <ClientOnly>
              <BaseSelect
                v-model="activeFilterId"
                :class="$style.filter"
                :options="computedCategories"
                :active-filter-value="computedCategoriesDefault"
                @on-change="onFilterChange"
              />
              <BaseSelect
                v-if="activeFilterId === 'article'"
                v-model="activeTagId"
                :class="$style.articleFilter"
                :placeholder="localizedStrings['Category'] || 'Category'"
                :options="computedTags"
                :clearable="true"
                :active-filter-value="computedTagsDefault"
                @on-change="onFilterChange"
              />
            </ClientOnly>
          </div>
          <div v-if="computedSortOptions.length > 1" :class="$style.filters">
            <span :class="[$style.filterLabel, 'u-type-p2']">
              {{
                data?.fields?.sortByLabel ||
                localizedStrings['sort-by'] ||
                'Sort By'
              }}
            </span>
            <ClientOnly>
              <BaseSelect
                v-model="activeSortValue"
                :class="$style.filter"
                :options="computedSortOptions"
                :active-filter-value="activeSortValue"
                @on-change="onFilterChange"
              />
            </ClientOnly>
          </div>
        </div>

        <div v-if="!searchValue.length" :class="$style.progress">
          {{ localizedStrings['noSearchValue'] || 'No Search Value' }}
        </div>
        <template v-else-if="searchMode === 'categorized'">
          <div
            v-if="
              articleLoading ||
              productLoading ||
              eventLoading ||
              marketingMaterialLoading ||
              documentLoading
            "
            :class="$style.progress"
          >
            <LoaderDots
              :aria-label="localizedStrings['loading'] || 'Loading'"
            />
          </div>
          <template
            v-else-if="
              articleSearchResults.length ||
              productSearchResults.length ||
              eventSearchResults.length ||
              marketingMaterialSearchResults.length ||
              documentSearchResults.length
            "
          >
            <SearchResultsCategory
              type="article"
              :view-all-button="data?.fields?.viewAllButton"
              :search-results="articleSearchResults"
              :title="localizedStrings['Article'] || 'Article'"
              :total="articleTotal"
              @request-view-all="() => switchSearchMode('article')"
            />
            <SearchResultsCategory
              type="product"
              :view-all-button="data?.fields?.viewAllButton"
              :search-results="productSearchResults"
              :title="localizedStrings['ProductDetailsPage'] || 'Product'"
              :total="productTotal"
              @request-view-all="() => switchSearchMode('product')"
            />
            <SearchResultsCategory
              type="event"
              :view-all-button="data?.fields?.viewAllButton"
              :search-results="eventSearchResults"
              :title="localizedStrings['Event'] || 'Event'"
              :total="eventTotal"
              @request-view-all="() => switchSearchMode('event')"
            />
            <SearchResultsCategory
              type="marketing-material"
              :view-all-button="data?.fields?.viewAllButton"
              :search-results="marketingMaterialSearchResults"
              :title="
                localizedStrings['marketing-material'] || 'Marketing Material'
              "
              :total="marketingMaterialTotal"
              @request-view-all="() => switchSearchMode('marketingMaterialSet')"
            />
            <SearchResultsCategory
              type="document"
              :view-all-button="data?.fields?.viewAllButton"
              :search-results="documentSearchResults"
              :title="
                localizedStrings['rules-and-documentation'] ||
                'Rules and Documentation'
              "
              :total="documentTotal"
              @request-view-all="() => switchSearchMode('downloadableDocument')"
            />
          </template>
          <div v-else :class="$style.progress">
            {{ localizedStrings['no-results'] || 'No Results' }}
          </div>
        </template>
        <template v-else-if="searchMode === 'single'">
          <div v-if="loading" :class="$style.progress">
            {{ localizedStrings['loading'] || 'Loading' }}
          </div>
          <template
            v-else-if="
              articleSearchResults?.length && activeFilterId === 'article'
            "
          >
            <ul :class="$style.listing">
              <li v-for="result in articleSearchResults" :key="result?.sys?.id">
                <SearchResultsCard :data="result" />
              </li>
            </ul>

            <CtaWings
              v-if="
                data?.fields?.loadMoreButton &&
                articleTotal > articleSearchResults?.length
              "
            >
              <CallToAction
                :cta="data?.fields?.loadMoreButton"
                @click="handleLoadMore('article')"
              />
            </CtaWings>
          </template>
          <template
            v-else-if="
              productSearchResults?.length && activeFilterId === 'product'
            "
          >
            <ul :class="$style.listing">
              <li v-for="result in productSearchResults" :key="result?.id">
                <SearchResultsCard type="product" :data="result" />
              </li>
            </ul>
          </template>
          <template
            v-else-if="
              marketingMaterialSearchResults?.length &&
              activeFilterId === 'marketingMaterialSet'
            "
          >
            <ul :class="$style.listing">
              <li
                v-for="result in marketingMaterialSearchResults"
                :key="result?.sys?.id"
              >
                <SearchResultsCard :data="result" />
                <!-- <SearchResultsCard
                  type="marketing-material"
                  :data="result"
                  variant="carousel"
                /> -->
              </li>
            </ul>

            <CtaWings
              v-if="
                data?.fields?.loadMoreButton &&
                marketingMaterialTotal > marketingMaterialSearchResults?.length
              "
            >
              <CallToAction
                :cta="data?.fields?.loadMoreButton"
                @click="handleLoadMore('marketingMaterial')"
              />
            </CtaWings>
          </template>
          <template v-else-if="searchResults?.length">
            <ul :class="$style.listing">
              <li v-for="result in searchResults" :key="result?.sys?.id">
                <SearchResultsCard :data="result" />
              </li>
            </ul>

            <CtaWings
              v-if="
                data?.fields?.loadMoreButton && total > searchResults?.length
              "
            >
              <CallToAction
                :cta="data?.fields?.loadMoreButton"
                @click="handleLoadMore"
              />
            </CtaWings>
          </template>
          <div v-else :class="$style.progress">
            {{ localizedStrings['no-results'] || 'No Results' }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ES_SUPPORTED_CONTENT_TYPE } from '~/utils/const'
import type {
  TypeArticleWithoutUnresolvableLinksResponse,
  TypeDownloadableDocumentWithoutUnresolvableLinksResponse,
  TypeEventWithoutUnresolvableLinksResponse,
  TypeMarketingMaterialSetWithoutUnresolvableLinksResponse,
  TypeProductDetailsPageSkeleton,
  TypeProductDetailsPageWithoutUnresolvableLinksResponse,
  TypeProductWithoutUnresolvableLinksResponse,
  TypeSearchResultsWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import type { EntriesQueries, Entry, EntrySkeletonType } from 'contentful'
import {
  fetchArticlesByIdsInBatches,
  filterAccessibleArticles,
} from '~/services/articles'
import type { TypeResultAssetData } from './SearchResultsAsset.vue'

const { $client, $userAccessCheck, $visibility } = useNuxtApp()
const { locale } = useI18n()
const route = useRoute()
const { localizedStrings } = useLocalizedStrings()

const cookieStore = useCookieStore()
const { highestUserLevel } = storeToRefs(useUserStore())

const GET_ENTRIES_LIMIT = 6

const props = defineProps<{
  data: TypeSearchResultsWithoutUnresolvableLinksResponse
}>()

export type TypeSearchResults =
  | TypeArticleWithoutUnresolvableLinksResponse
  | TypeProductDetailsPageWithoutUnresolvableLinksResponse
  | TypeProductWithoutUnresolvableLinksResponse
  | TypeEventWithoutUnresolvableLinksResponse
  | TypeMarketingMaterialSetWithoutUnresolvableLinksResponse
  | TypeDownloadableDocumentWithoutUnresolvableLinksResponse
  | TypeResultAssetData
  | Entry<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  | undefined

const loading = ref(true)
const articleLoading = ref(true)
const productLoading = ref(true)
const eventLoading = ref(true)
const marketingMaterialLoading = ref(true)
const documentLoading = ref(true)
const currentPage = ref(1)

const total = ref(0)
const articleTotal = ref(0)
const productTotal = ref(0)
const eventTotal = ref(0)
const marketingMaterialTotal = ref(0)
const documentTotal = ref(0)

const ignoreWatch = ref(true) // Used to ignore watch temporarily when switching search modes
const searchMode = ref<'categorized' | 'single'>('categorized')
const searchValue = ref(arrayAsString(route.query.q) || '')
const activeFilterId = ref('all')
const activeTagId = ref(arrayAsString(route.query.tag) || '')
const activeSortValue = ref('latest')

const searchResults = ref<TypeSearchResults[]>([])
const allArticleSearchResults = ref<TypeSearchResults[]>([])
const productSearchResults = ref<TypeResultAssetData[]>([])
const eventSearchResults = ref<TypeSearchResults[]>([])
const marketingMaterialSearchResults = ref<TypeSearchResults[]>([])
const documentSearchResults = ref<TypeSearchResults[]>([])
const articleSearchResults = computed(() =>
  allArticleSearchResults.value.slice(0, currentPage.value * GET_ENTRIES_LIMIT),
)

const fetchSearchEntryIds = async (
  type: string = activeFilterId.value,
  options: {
    from?: number
    size?: number
  } = {},
) => {
  let searchEndpoint = `/api/search?`
  if (locale.value) searchEndpoint += `lang=${locale.value}`
  if (cookieStore.countryCookie)
    searchEndpoint += `&country=${cookieStore.countryCookie}`
  if (searchMode.value === 'single' && activeSortValue.value)
    searchEndpoint += `&sort=${activeSortValue.value}`
  if (searchValue.value)
    searchEndpoint += `&q=${encodeURIComponent(searchValue.value)}`

  const size = options.size ?? GET_ENTRIES_LIMIT
  const from =
    options.from ??
    (searchMode.value === 'single' ? searchResults.value?.length || 0 : 0)

  if (size) searchEndpoint += `&size=${size}`
  if (from) searchEndpoint += `&from=${from}`
  if (type && type !== 'all') searchEndpoint += `&type=${type}`
  if (type === 'article' && activeTagId.value)
    searchEndpoint += `&tag=${encodeURIComponent(activeTagId.value)}`

  return $fetch<{
    ids: string[]
    total: {
      value: number
      relation: string
    }
  }>(searchEndpoint)
}

const searchContentful = async (contentType: string) => {
  try {
    const q = (searchValue.value || '').trim()
    if (!q) {
      return { items: [], total: 0 }
    }

    let query: EntriesQueries<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS'> =
      {
        content_type: contentType,
        'fields.title[match]': q,
        locale: locale.value,
        limit: GET_ENTRIES_LIMIT,
        include: 10,
      }

    if (contentType === 'downloadableAsset') {
      query = {
        ...query,
        skip: marketingMaterialSearchResults.value?.length || 0,
      }
    }

    const response = await $client.withoutUnresolvableLinks.getEntries(query)

    // Keep consistent return shape with your ES path
    return {
      items: response?.items || [],
      total:
        typeof response?.total === 'number'
          ? response.total
          : response?.items?.length || 0,
    }
  } catch (e) {
    console.log('[searchContentful] error', e)
    return { items: [], total: 0 }
  }
}

const search = async (
  type: string = activeFilterId.value,
  options: {
    from?: number
    size?: number
  } = {},
) => {
  try {
    const resultEntryIds = await fetchSearchEntryIds(type, options)

    // If no results, return early
    if (!resultEntryIds?.ids?.length) return { items: [], total: 0 }

    const response = await $client.withoutUnresolvableLinks.getEntries({
      'sys.id[in]': [resultEntryIds?.ids?.join(',')], // weird typing issue with contentful sdk, normally should be string joined by comma
      locale: locale.value,
      limit: 500,
      include: 10,
    })

    // Entries we get back from contentful are not always in the same
    // order as the search results. So we reorder based on the search's
    // response
    const sortedEntries = (resultEntryIds?.ids || [])
      .map?.((id) => response?.items?.find?.((entry) => entry?.sys?.id === id))
      .filter(Boolean)

    return { items: sortedEntries, total: resultEntryIds?.total?.value }
  } catch (e) {
    console.log(e)
  }
}

const searchArticles = async () => {
  try {
    const q = (searchValue.value || '').trim()
    if (!q) {
      return { items: [], total: 0 }
    }

    const pageSize = 100
    let from = 0
    let total = 0
    const entryIds: string[] = []

    do {
      const response = await fetchSearchEntryIds('article', {
        from,
        size: pageSize,
      })
      const ids = response?.ids || []

      if (!ids.length) break

      total = response?.total?.value || 0
      entryIds.push(...ids)
      from += ids.length
    } while (entryIds.length < total)

    if (!entryIds.length) {
      return { items: [], total: 0 }
    }

    const hydratedArticles = await fetchArticlesByIdsInBatches({
      $client,
      locale: locale.value,
      ids: entryIds,
    })

    const accessibleArticles = filterAccessibleArticles({
      articles: hydratedArticles,
      $userAccessCheck,
      $visibility,
      selectedCategory: activeTagId.value,
      highestUserLevel: highestUserLevel.value,
    })

    const orderMap = new Map(entryIds.map((id, index) => [id, index]))
    const sortedArticles = accessibleArticles
      .slice()
      .sort(
        (a, b) => (orderMap.get(a.sys.id) ?? 0) - (orderMap.get(b.sys.id) ?? 0),
      )

    return {
      items: sortedArticles,
      total: sortedArticles.length,
    }
  } catch (e) {
    console.log('[searchArticles] error', e)
    return { items: [], total: 0 }
  }
}

const isProductAccessible = (
  product: TypeProductWithoutUnresolvableLinksResponse,
) =>
  $visibility(product.fields?.visibility) &&
  $userAccessCheck(product.fields?.accessLevel, highestUserLevel.value)

const createProductSearchResult = (
  product: TypeProductWithoutUnresolvableLinksResponse,
  path: string,
): TypeResultAssetData => ({
  sys: {
    id: product.sys.id,
    contentType: {
      sys: {
        id: 'product',
      },
    },
  },
  id: product.sys.id,
  image: product.fields?.images?.[0],
  title: product.fields?.name,
  link: `/products/${path}?product=${product.sys.id}`,
})

const searchProducts = async () => {
  try {
    const q = (searchValue.value || '').trim()
    if (!q) {
      return { items: [], total: 0 }
    }

    const productResults = await search('product', { size: 100 })
    const matchedProducts = (productResults?.items || []).filter(
      instanceOfProduct,
    )
    const resolvedResults: TypeResultAssetData[] = []

    const matchedProductPageResponse = matchedProducts.length
      ? await $client.withoutUnresolvableLinks.getEntries<TypeProductDetailsPageSkeleton>(
          {
            content_type: 'productDetailsPage',
            locale: locale.value,
            include: 2,
            limit: matchedProducts.length,
            'fields.products.sys.id[in]': matchedProducts
              .map((product) => product.sys.id)
              .join(','),
          },
        )
      : { items: [] }

    const productPathById = new Map<string, string>()

    matchedProductPageResponse.items
      .filter(instanceOfProductDetailsPage)
      .forEach((page) => {
        const path = page.fields?.path
        const isPageAccessible =
          $visibility(page.fields?.visibility) &&
          $userAccessCheck(page.fields?.accessLevel, highestUserLevel.value)
        if (!path || !isPageAccessible) return

        page.fields?.products?.filter(instanceOfProduct).forEach((product) => {
          if (!productPathById.has(product.sys.id)) {
            productPathById.set(product.sys.id, path)
          }
        })
      })

    matchedProducts.filter(isProductAccessible).forEach((product) => {
      const path = productPathById.get(product.sys.id)
      if (!path) return

      resolvedResults.push(createProductSearchResult(product, path))
    })

    return {
      items: resolvedResults,
      total: resolvedResults.length,
    }
  } catch (e) {
    console.log('[searchProducts] error', e)
    return { items: [], total: 0 }
  }
}

const { execute: fetchSearchResults } = await useLazyAsyncData(
  `search-results-data-${cookieStore.countryCookie}-${cookieStore.languageCookie}-${searchValue.value}-${activeSortValue.value}-${activeFilterId.value}`,
  async () => search(),
  {
    transform(input) {
      searchResults.value = [
        ...(searchResults.value ? searchResults.value : []),
        ...(input?.items ? input.items : []),
      ]
      total.value = input?.total || 0
      loading.value = false
      return {
        results: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    immediate: false,
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)
const { execute: fetchArticleSearchResults } = await useLazyAsyncData(
  `search-results-data-${cookieStore.countryCookie}-${cookieStore.languageCookie}-${searchValue.value}-${activeTagId.value}-${activeSortValue.value}-article`,
  async () => searchArticles(),
  {
    transform(input) {
      allArticleSearchResults.value = input?.items || []
      articleTotal.value = input?.total || 0
      articleLoading.value = false

      // Also set general loading flag to false, since this content type's
      // search results are fetched differently
      if (searchMode.value === 'single') loading.value = false

      return {
        results: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    immediate: false,
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)
const { execute: fetchProductSearchResults } = await useLazyAsyncData(
  `search-results-data-${cookieStore.countryCookie}-${cookieStore.languageCookie}-${searchValue.value}-product`,
  async () => searchProducts(),
  {
    transform(input) {
      productSearchResults.value = input?.items || []
      productTotal.value = input?.total || 0
      productLoading.value = false

      if (searchMode.value === 'single') loading.value = false

      return {
        results: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    immediate: false,
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)
const { execute: fetchEventSearchResults } = await useLazyAsyncData(
  `search-results-data-${cookieStore.countryCookie}-${cookieStore.languageCookie}-${searchValue.value}-event`,
  async () => search('event'),
  {
    transform(input) {
      eventSearchResults.value = input?.items || []
      eventTotal.value = input?.total || 0
      eventLoading.value = false
      return {
        results: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    immediate: false,
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)
const { execute: fetchMarketingMaterialSearchResults } = await useLazyAsyncData(
  `search-results-data-${cookieStore.countryCookie}-${cookieStore.languageCookie}-${searchValue.value}-marketingMaterialSet`,
  async () => searchContentful('downloadableAsset'), // NOTE: using contentful for now as ES doesn't have this index yet
  {
    transform(input) {
      const existingResults = marketingMaterialSearchResults.value || []
      marketingMaterialSearchResults.value = [
        ...existingResults,
        ...(input?.items || []),
      ]
      marketingMaterialTotal.value = input?.total || 0
      marketingMaterialLoading.value = false

      // Also set general loading flag to false, since this content type's
      // search results are fetched differently
      if (searchMode.value === 'single') loading.value = false

      return {
        results: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    // getCachedData: (key) => useCachedData(key),
    immediate: false,
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)
const { execute: fetchDocumentSearchResults } = await useLazyAsyncData(
  `search-results-data-${cookieStore.countryCookie}-${cookieStore.languageCookie}-${searchValue.value}-downloadableDocument`,
  async () => search('downloadableDocument'),
  {
    transform(input) {
      documentSearchResults.value = input?.items || []
      documentTotal.value = input?.total || 0
      documentLoading.value = false
      return {
        results: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    immediate: false,
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)

const computedCategories = computed(() => {
  return ES_SUPPORTED_CONTENT_TYPE?.length
    ? [
        {
          label: localizedStrings.value['all'] || 'All',
          value: 'all',
        },
        ...ES_SUPPORTED_CONTENT_TYPE,
      ]
    : []
})

const computedCategoriesDefault = computed(() => {
  const filterValue = route.query.filter || activeFilterId.value

  const activeFilter = computedCategories.value?.find?.(
    ({ value }) => filterValue === value,
  )

  return activeFilter
    ? activeFilter.value
    : computedCategories.value?.[0]?.value
})

const computedTags = computed(() => {
  const tags = props?.data?.fields?.articleCategories?.filter(instanceOfTag)
  return tags
    ? [
        ...tags.map((tag) => ({
          label: tag?.fields?.label || '',
          value: tag?.fields?.value || '',
        })),
      ]
    : []
})

const computedTagsDefault = computed(() => {
  const filterValue = route.query.tag || activeTagId.value

  const activeTag = computedTags.value?.find?.(
    ({ value }) => filterValue === value,
  )

  return activeTag ? activeTag.value : ''
})

const computedSortOptions = computed(() => {
  return [
    {
      label: 'Relevance',
      value: 'relevance',
    },
    {
      label: 'Latest',
      value: 'latest',
    },
  ]
})

const setQuery = (filter?: string) => {
  const router = useRouter()
  router.push({
    query: {
      ...route.query,
      q: searchValue.value || undefined,
      sort:
        searchMode.value === 'single'
          ? activeSortValue.value || undefined
          : undefined,
      filter:
        searchMode.value === 'single'
          ? filter || activeFilterId.value || undefined
          : undefined,
      tag:
        searchMode.value === 'single'
          ? activeTagId.value || undefined
          : undefined,
    },
  })
}

const handleSearch = (val: string) => {
  if (val === searchValue.value) return
  searchMode.value = 'categorized'
  ignoreWatch.value = true

  articleLoading.value = true
  productLoading.value = true
  eventLoading.value = true
  marketingMaterialLoading.value = true
  documentLoading.value = true

  allArticleSearchResults.value = []
  productSearchResults.value = []
  eventSearchResults.value = []
  marketingMaterialSearchResults.value = []
  documentSearchResults.value = []

  searchValue.value = val || ''
  currentPage.value = 1

  fetchArticleSearchResults()
  fetchProductSearchResults()
  fetchEventSearchResults()
  fetchMarketingMaterialSearchResults()
  fetchDocumentSearchResults()

  setQuery()
}

const setInitial = () => {
  // make sure we are on client side
  if (import.meta.server) return

  // scroll to top of results
  window.scrollTo({ top: 0, behavior: 'smooth' })

  searchResults.value = []
  loading.value = true
  total.value = 0
}

watch([activeFilterId, activeTagId, activeSortValue], () => {
  if (!ignoreWatch.value) {
    setInitial()
    currentPage.value = 1
    if (activeFilterId.value === 'article') {
      articleLoading.value = true
      fetchArticleSearchResults()
    } else if (activeFilterId.value === 'product') {
      productLoading.value = true
      fetchProductSearchResults()
    } else if (activeFilterId.value === 'marketingMaterialSet') {
      marketingMaterialLoading.value = true
      fetchMarketingMaterialSearchResults()
    } else {
      fetchSearchResults()
    }
    setQuery()
  }
})

const handleLoadMore = (type: string) => {
  if (type === 'article') {
    currentPage.value = currentPage.value + 1
  } else if (type === 'product') {
    fetchProductSearchResults()
  } else if (type === 'event') {
    fetchEventSearchResults()
  } else if (type === 'marketingMaterial') {
    fetchMarketingMaterialSearchResults()
  } else if (type === 'document') {
    fetchDocumentSearchResults()
  } else {
    fetchSearchResults()
  }
}

const switchSearchMode = (filter: string) => {
  searchMode.value = 'single'
  activeFilterId.value = filter
  ignoreWatch.value = true

  // Trigger general search here to avoid default values from overwriting filter
  setInitial()
  if (filter === 'article') {
    currentPage.value = 1
    fetchArticleSearchResults()
  } else if (filter === 'product') {
    fetchProductSearchResults()
  } else if (filter === 'marketingMaterialSet') {
    fetchMarketingMaterialSearchResults()
    searchResults.value = marketingMaterialSearchResults.value
  } else {
    fetchSearchResults()
  }
  setQuery(filter)
}

const onFilterChange = async () => {
  await nextTick()
  ignoreWatch.value = false
}

const hasFilterInUrl = computed(() => {
  const f = arrayAsString(route.query.filter)
  // treat any non-empty string (including 'all') as "single"
  return typeof f === 'string' && f.length > 0
})

// validate filter against computedCategories
const normalizeFilter = (raw: string | undefined) => {
  const val = raw || ''
  const exists = computedCategories.value.some((opt) => opt.value === val)
  return exists ? val : 'all'
}

// onMounted(() => {
//   if (route?.query?.filter)
//     activeFilterId.value = arrayAsString(route.query.filter) || ''
//   if (route?.query?.sort)
//     activeSortValue.value = arrayAsString(route.query.sort) || ''

//   fetchArticleSearchResults()
//   fetchProductSearchResults()
//   fetchEventSearchResults()
//   fetchMarketingMaterialSearchResults()
//   fetchDocumentSearchResults()
// })

watch(
  () => [route.query.q, route.query.sort, route.query.filter, route.query.tag],
  async ([q, sort, filter, tag]) => {
    // sync local state from URL first
    searchValue.value = arrayAsString(q) || ''
    activeTagId.value = arrayAsString(tag) || ''
    activeSortValue.value = arrayAsString(sort) || 'latest'
    currentPage.value = 1

    ignoreWatch.value = true
    activeFilterId.value = normalizeFilter(arrayAsString(filter))
    searchMode.value = hasFilterInUrl.value ? 'single' : 'categorized'

    if (!searchValue.value.length) {
      // no search string -> show "No Search Value" state and bail
      setInitial()
      allArticleSearchResults.value = []
      productSearchResults.value = []
      eventSearchResults.value = []
      marketingMaterialSearchResults.value = []
      documentSearchResults.value = []
      return
    }

    if (searchMode.value === 'single') {
      // single-mode: run the unified search
      setInitial()
      if (activeFilterId.value === 'article') {
        fetchArticleSearchResults()
      } else if (activeFilterId.value === 'product') {
        fetchProductSearchResults()
      } else if (activeFilterId.value === 'marketingMaterialSet') {
        fetchMarketingMaterialSearchResults()
        searchResults.value = marketingMaterialSearchResults.value
      } else {
        fetchSearchResults()
      }
    } else {
      // categorized-mode: (re)load all buckets
      articleLoading.value = true
      productLoading.value = true
      eventLoading.value = true
      marketingMaterialLoading.value = true
      documentLoading.value = true

      allArticleSearchResults.value = []
      productSearchResults.value = []
      eventSearchResults.value = []
      marketingMaterialSearchResults.value = []
      documentSearchResults.value = []

      // fire them; no need to await in series
      fetchArticleSearchResults()
      fetchProductSearchResults()
      fetchEventSearchResults()
      fetchMarketingMaterialSearchResults()
      fetchDocumentSearchResults()
    }
  },
  { immediate: true },
)
</script>

<style lang="scss" module>
.container {
  max-width: 1440px;
  padding: 0 20px;
  margin: auto;
}

.searchBlock {
  background: #fff;
  padding: 50px 0;
  margin-bottom: 42px;
}

.search.search {
  width: 100%;
}

.progress {
  padding: 50px;
  text-align: center;
}

.listing {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 17px;
}

.cta {
  margin-top: 40px;
}

.options {
  display: flex;
  flex-wrap: wrap;
  justify-content: stretch;
  gap: 20px;
  width: 100%;
  margin-bottom: 17px;

  @include media('sm') {
    justify-content: flex-start;
    column-gap: 40px;
    row-gap: 20px;
  }
}

.filters {
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: center;
  justify-content: flex-start;
  gap: 18px;
  width: 100%;

  @include media('sm') {
    grid-template-columns: auto 250px;
    width: auto;
  }
}

.articleFilters {
  @include media('sm') {
    grid-template-columns: auto 250px 250px;
  }
}

.articleFilter {
  grid-area: 2 / 2;

  @include media('sm') {
    grid-area: auto;
  }
}

.filterLabel {
  @include fluid-type(16px, 19px);
  line-height: math.div(30, 19);
}

.resultsBlock {
  min-height: 50vh;
  margin-bottom: 43px;
  overflow-x: clip;

  :global .swiper {
    overflow: visible;
    @include media('xl') {
      overflow-x: clip;
    }
  }
}

.title {
  font-family: var(--roboto-condensed);
  font-size: to-rem(26px);
  font-weight: bold;
  line-height: math.div(32, 26);

  @include media('md') {
    font-size: to-rem(42px);
    line-height: math.div(46, 24);
  }
}
</style>
